import { createPortal } from "react-dom";
import moment from "moment";

import { IndividualLight } from "@/Common/Api/Individuals/Dto/IndividualLight";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { AttachedCommunicationsPopup } from "@/Common/Components/AttachedCommunications/AttachedCommunicationsPopup";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnInit, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { AttachedCommunicationType } from "@/Common/Models/AttachedCommunication/AttachedCommunicationType";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { DocumentSortOrderType, SortDirection } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseDocumentSearch, BaseDocumentSearchState, IBaseDocumentSearchProps } from "@/Documents/Components/DocumentSearch/BaseDocumentSearch";
import { DocumentSettingsContainer } from "@/Documents/Components/DocumentSearch/DocumentSettingsContainer";
import { DocumentsHelper } from "@/Documents/Helpers/DocumentsHelper";
import { MedicalDocumentTableItem } from "@/Documents/Models/Medical/MedicalDocumentTableItem";
import { SendDocumentStrategy } from "@/Documents/Services/Portal/Strategies/SendDocumentStrategy";
import { BcCareConnectButton } from "@/Patient/Components/BcCareConnectButton";
import { BcCareConnectDomain } from "@/Patient/Models/BcCareConnect/BcCareConnect";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";

import styles from "@/Documents/Components/DocumentSearch/DocumentSettingsContainer.module.scss";

class PatientDocumentSearch extends BaseDocumentSearch {
    protected helper: MyleHelper = new MyleHelper();
    private isSelectAllDocument: boolean = false;
    private tableCellClass: string = "text-wrap-control";
    private saveOptionKey: string = "DocumentsDisplaySettingsPatientView";
    private isPortalEnabled: boolean = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;

    constructor(props: IBaseDocumentSearchProps) {
        super(props);
    }

    public render(): JSX.Element {
        const canApprove = sessionStore.canApproveDocument();

        const columnsOptions = this.helper
            .loadTableColumnOptionsFromSettings(this.saveOptionKey, this.defineInitialColumnsForTable()).filter(c => c.FieldName !== "Patients")
            .toList();

        const tableRenderOptions = this.tableRenderingOptions(columnsOptions);
        const documentsTableColumns = this.getTableColumns(columnsOptions);
        const arrMarkedForApproval: string[] = [];

        this.props.Model.get("DocumentsTableData")
            .forEach(item => {
                if (item.IsSelected)
                    arrMarkedForApproval.push(item.Model.Model.Id);
            });

        let approvedButton: JSX.Element = null;
        if (arrMarkedForApproval.length > 0 && canApprove) {
            let ApproveClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
                e.preventDefault();
                let operation = new LocalEvent(LocalOperations.Documents.Approve);
                operation.AddItem("DocumentIDs", arrMarkedForApproval);
                LocalEventDispatcher.dispatch(operation);
            };

            approvedButton = (
                <button className="btn btn-primary" data-cy="patientDocumentSearch-approve" onClick={e => ApproveClick(e)}>{cacheStore.GetString("ApproveLabel")}</button>
            );
        }
        else {
            approvedButton = (
                <button className="btn btn-primary disabled">{cacheStore.GetString("ApproveLabel")}</button>
            );
        }

        if (this.props.Model.get("UpdatedTableColumn")) {
            let u = this.props.Model.get("UpdatedTableColumn");

            let idx = documentsTableColumns.findIndex(column => column.columnOptions.FieldName === u.columnOptions.FieldName);
            if (idx >= 0) {
                // deactivate active sort for all columns
                for (let i = 1; i < documentsTableColumns.size - 1; i++) { // start at one, don't mess with approve column
                    const column = documentsTableColumns.get(i);
                    if (column.columnOptions.FieldName !== u.columnOptions.FieldName) {
                        column.columnOptions.IsSortActive = false;
                        column.ResetHeader();
                    }
                }

                // set new active sort
                const activeColumn = documentsTableColumns.get(idx);
                activeColumn.columnOptions.IsSortActive = true;
                activeColumn.ResetHeader();
            }
        }

        let searchOptionsControl: JSX.Element = null;
        const hasPortalAccess = cacheStore.ClinicSettings_Portal.EnablePortalWebsite;

        if (this.props.Model.get("SearchOptionsOpen")) {
            searchOptionsControl = (
                createPortal(
                    <div
                        id="documents_search_options_container"
                        onClick={this.onContainerClick}
                        className={`filter-component filter-component-responsive ${(hasPortalAccess) ? "portal" : ""} ${styles.filterComponent}`}
                    >
                        <DocumentSettingsContainer Model={this.props.Model} />
                    </div>,
                    document.body
                )
            );
        }

        return this.nativeView(searchOptionsControl, approvedButton, tableRenderOptions, documentsTableColumns, columnsOptions);
    }

    public mutateState(mutation: (s: BaseDocumentSearchState) => void, then: any = null): void {
        let s = new BaseDocumentSearchState();
        mutation(s);
        this.setState(s, then);
    }

    private nativeView(searchOptionsControl: JSX.Element, approvedButton: JSX.Element, tableRenderOptions: ITableRenderOptions, documentsTableColumns: Immutable.List<MyleTableColumn>, columnsOptions: Immutable.List<MyleTableColumnOptions>): JSX.Element {
        const colConfig: ColumnConfigurationModel[] = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="sb-workspace inherit-height">
                {this.props.Model.get("IsSendToPatientPortalPopupOpen") && (
                    <SendToPortalPopup
                        model={this.props.Model.get("PortalLinkedItemModel")}
                        strategy={new SendDocumentStrategy()}
                    />
                )}
                {this.props.Model.get("AttachedCommunicationsPopupOpen") && (
                    <AttachedCommunicationsPopup
                        controlId="attached_communication_documentsearch"
                        topPosition={this.props.Model.get("AttachedCommunicationsPopupY")}
                        leftPosition={this.props.Model.get("AttachedCommunicationsPopupX")}
                        attachedCommunications={this.props.Model.get("AttachedCommunicationsPopupList")}
                        context={AttachedCommunicationType.Task}
                        handleRedirect={ac => this.taskRedirectClick(ac)}
                        handleClose={() => this.taskRedirectClose()}
                    />
                )}
                {this.state.showDocumentViewSettings &&
                    this.renderDocumentViewSettingPopup()
                }
                <CommonTableHeaderComponent
                    title={this.getDocumentsTitle(this.props.Model)}
                    additionalLeftComponent={this.renderTitleButtons()}
                    searchText={this.props.Model.get("GlobalDocumentSearchText")}
                    searchPlaceholder={cacheStore.GetString("DocumentSearchLabel")}
                    onSearch={text => this.FilterTextChanged(text)}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.Documents.SearchSettings_OnRefreshClick)}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.Documents.SearchSettings_ShowOptionsButtonClick)}
                />
                <div className="tb ws-scrollable documentSearch">
                    {searchOptionsControl}
                    <div className="myle-doc-tb main-content table-wrapper">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="doc_main_tb"
                            TableDataCy="patient-documentTable"
                            Columns={Immutable.List<MyleTableColumn>(documentsTableColumns)}
                            DataSource={this.props.Model.get("DocumentsTableData")}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickFunction={(medicalDocumentTableItem, event) => this.onDocumentSelected(medicalDocumentTableItem, event)}
                            SaveOptionskeyTarget={this.saveOptionKey}
                            ColumnReorderTarget={MyleTableTarget.Myle_Documents_SearchResults_ColumnReorder}
                            ColumnSortTarget={MyleTableTarget.Myle_Documents_SearchResults_ColumnSortClick}
                        />
                    </div>
                    <div className="clearfix" />
                </div>
                <div className="ws-subfooter table-pager-footer">
                    <div className="row">
                        <div className="col-lg-10 col-md-9 col-sm-8">
                            <SimplePagerControl
                                PageSize={this.props.Model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.Model.get("TotalQueryRows")}
                                CurrentPage={this.props.Model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.Documents.Search_ChangePage}
                            />
                        </div>
                        <div className="col-lg-2 col-md-3 col-sm-4 text-right">
                            {approvedButton}
                        </div>
                    </div>
                </div>

                {this.props.Model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={colConfig}
                        SaveOptionskeyTarget={this.saveOptionKey}
                        ContainerId="docsColumnSettingsPopup"
                        CloseColumOrder={LocalOperations.Documents.DisplaySettings_CloseColumOrderPopupClick}
                    />
                )}
            </div>
        );
    }

    private renderTitleButtons(): JSX.Element[] {
        const buttons: JSX.Element[] = [];

        if (patientStore.isDSQActive() || sessionStore.isBcCareConnectActive()) {
            buttons.push(
                <div
                    style={{ display: "inherit", alignItems: "center", flexWrap: "nowrap", gap: "10px" }}
                    key="dsqAndCareConnectButtons"
                >
                    {patientStore.isDSQActive() && this.renderDsqButtons()}
                    {sessionStore.isBcCareConnectActive() && this.renderBcCareConnectButton()}
                </div>
            );
        }

        return buttons;
    }

    private renderDsqButtons(): JSX.Element[] {
        return [
            <ButtonControl
                key="dsqlabs"
                className="btn btn-primary"
                onClick={() => this.onDSQLabsClick()}
                label={cacheStore.GetString("btnDSQLabsInbox")}
            />,
            <ButtonControl
                key="dsqimgs"
                className="btn btn-primary"
                onClick={() => this.onDSQImgsClick()}
                label={cacheStore.GetString("btnDSQImgsInbox")}
            />
        ];
    }

    private renderBcCareConnectButton(): JSX.Element {
        return (
            <BcCareConnectButton
                key="careconnect"
                patient={this.props.patient}
                domain={BcCareConnectDomain.Documents}
            />
        );
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MedicalDocumentTableItem>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Approve":
                        return this.buildApproveColumn();

                    case "ReceiptDate":
                        return this.buildReceiptDateColumn();

                    case "UploadDate":
                        return this.buildUploadDateColumn();

                    case "Type":
                        return this.buildTypeColumn();

                    case "Description":
                        return this.buildDescriptionColumn();

                    case "Doctor":
                        return this.buildDoctorColumn();

                    case "Status":
                        return this.buildStatusColumn();

                    case "PortalStatus":
                        if (this.isPortalEnabled)
                            return this.buildPortalStatusColumn();
                        break;

                    case "Action":
                        return this.buildActionColumn();

                    case "Site":
                        return this.buildSiteColumn();
                }
            },
            false
        );
    }

    // Approve
    private buildApproveColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                if (item.Model.Model.CanBeApprovedByCurrentUser) {
                    return (
                        <div>
                            <input
                                type="checkbox"
                                id={item.Model.Model.Id}
                                onChange={() => this.SetApproveSelectedDocuments(item)}
                                checked={item.IsSelected}
                            />
                        </div>
                    );
                }
                else
                    return <div />;
            },
            renderHeader: () => (
                <div className="checkbox-col">
                    <input
                        type="checkbox"
                        className="header-input"
                        onChange={() => this.SetApproveSelectedToAllDocuments()}
                        checked={this.isSelectAllDocument}
                    />
                </div>
            )
        };
    }

    // ReceiptDate
    private buildReceiptDateColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                let date = moment(item.Model.Model.ReceiptDate);
                return (
                    <div title={date.format("YYYY/MM/D")} className={item.Model.Model.IsUrgent ? "red-row" : ""}>
                        <span className={this.tableCellClass}>
                            {date.format("YYYY/MM/D")}
                        </span>
                    </div>
                );
            }
        };
    }

    // UploadDate
    private buildUploadDateColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                let momentUploadDateList = item.Model.Model.DocumentPathList
                    .map(x => x.CreationDate || item.Model.Model.UploadDate);
                let lastUploadDate = (momentUploadDateList && momentUploadDateList.length > 0)
                    ? moment(momentUploadDateList.sort().reverse()[0])
                    : moment(item.Model.Model.UploadDate);

                return (
                    <div title={lastUploadDate.format("YYYY/MM/D")} className={item.Model.Model.UploadDate ? "red-row" : ""}>
                        <span className={this.tableCellClass}>
                            {lastUploadDate.format("YYYY/MM/D")}
                        </span>
                    </div>
                );
            }
        };
    }

    // Type
    private buildTypeColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                let classId = cacheStore.GetLookupById(item.Model.Model.ClassId);
                let subclassId = cacheStore.GetLookupById(item.Model.Model.SubClassId);
                let lookupclass = classId
                    ? cacheStore.GetLookupById(item.Model.Model.ClassId)
                    : new FlatLookupView();
                let lookupSubclass = subclassId
                    ? cacheStore.GetLookupById(item.Model.Model.SubClassId)
                    : new FlatLookupView();

                let classModel = DocumentClassModel.FromLookup(lookupclass);
                let subClassModel = DocumentClassModel.FromLookup(lookupSubclass);

                return (
                    <div title={classModel.Name + " - " + subClassModel.Name} className={item.Model.Model.IsUrgent ? "red-row" : ""}>
                        <span className={this.tableCellClass}>
                            {classModel.Name + " - " + subClassModel.Name}
                        </span>
                    </div>
                );
            }
        };
    }

    // Description
    private buildDescriptionColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => (
                <div title={item.Model.Model.Subject} className={item.Model.Model.IsUrgent ? "red-row" : ""}>
                    <span className={this.tableCellClass}>
                        {item.Model.Model.Subject}
                    </span>
                </div>
            )
        };
    }

    // Doctor
    private buildDoctorColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                let arrDoctors: JSX.Element[] = [];
                let providers = item.Model.Model.ResponsibleProviders;
                let results: IndividualLight[] = [];

                // remove duplicates (?)
                if (providers) {
                    for (let i = 0; i <= providers.length; i++) {
                        let unique = [];
                        for (let provider of providers) {
                            if (unique.indexOf(provider.Id) == -1) {
                                unique.push(provider.Id);
                                results.push(provider);
                            }
                        }
                    }
                }

                arrDoctors = providers.map(provider => {
                    let prefix = "";

                    let prefixLookup = cacheStore.GetLookupById(provider.Prefix);
                    if (prefixLookup != null)
                        prefix = cacheStore.GetLocalizedValueByLookupId(prefixLookup.LookupID) + " ";

                    let formattedName = prefix + provider.LastName + ", " + provider.FirstName;

                    return (
                        <div key={provider.Id} title={formattedName}>
                            {formattedName}
                        </div>
                    );
                });

                return (
                    <div className={item.Model.Model.IsUrgent ? "red-row" : ""}>
                        <span className={this.tableCellClass}>
                            {arrDoctors}
                        </span>
                    </div>
                );
            }
        };
    }

    // Status
    private buildStatusColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                const documentStatus = DocumentsHelper.GetDocumentStatusLabel(item.Model.Model);

                return (
                    <div title={documentStatus} className={item.Model.Model.IsUrgent ? "red-row" : ""}>
                        <span className={this.tableCellClass}>
                            {documentStatus}
                        </span>
                    </div>
                );
            }
        };
    }

    // Portal Status
    private buildPortalStatusColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                if (!PortalHelper.shouldShowPortalShareButton(item.Model.Model.Patients))
                    return <div />;

                const buttonProps = PortalHelper.getPortalButtonPropsForShareableItem(
                    item.Model
                );

                return (
                    <PortalStatusButton
                        icon="share"
                        onClick={e => this.PortalStatusClicked(item, e)}
                        {...buttonProps}
                    />
                );
            }
        };
    }

    // Site
    private buildSiteColumn(): MyleTableColumnInit<MedicalDocumentTableItem> {
        return {
            renderCell: item => {
                let DisplaySite = "";
                let site = cacheStore.GetSiteById(item.Model.Model.SiteId);
                if (site != null)
                    DisplaySite = site.Model.ClinicalName;

                return (
                    <div title={DisplaySite} className={item.Model.Model.IsUrgent ? "red-row" : ""}>
                        <span className={this.tableCellClass}>{DisplaySite} </span>
                    </div>
                );
            }
        };
    }

    private defineInitialColumnsForTable(): MyleTableColumnOptions[] {
        let cols: MyleTableColumnOptions[] = [];

        let c_opt1 = new MyleTableColumnOptions();
        c_opt1.HeaderText = cacheStore.GetString("ApproveLabel");
        c_opt1.FieldName = "Approve";
        c_opt1.WidthPixels = 38;
        c_opt1.IsFixedInPlace = true;

        let c_opt2 = new MyleTableColumnOptions();
        c_opt2.HeaderText = cacheStore.GetString("AttachedOnLabel");
        c_opt2.FieldName = "UploadDate";
        c_opt2.RowClickEnabled = true;
        c_opt2.IsSortable = true;
        c_opt2.SortColumn = DocumentSortOrderType.AttachedOnDate;
        c_opt2.SortDirection = SortDirection.ASC;
        c_opt2.WidthPercentageDefault = 5;

        let c_opt3 = new MyleTableColumnOptions();
        c_opt3.HeaderText = cacheStore.GetString("DocumentDateLabel");
        c_opt3.FieldName = "ReceiptDate";
        c_opt3.RowClickEnabled = true;
        c_opt3.IsSortable = true;
        c_opt3.SortColumn = DocumentSortOrderType.DocumentDate;
        c_opt3.SortDirection = SortDirection.ASC;
        c_opt3.WidthPercentageDefault = 8;

        let c_opt5 = new MyleTableColumnOptions();
        c_opt5.HeaderText = cacheStore.GetString("ClassLabel");
        c_opt5.FieldName = "Type";
        c_opt5.RowClickEnabled = true;
        c_opt5.IsSortable = true;
        c_opt5.SortColumn = DocumentSortOrderType.Type;
        c_opt5.SortDirection = SortDirection.ASC;
        c_opt5.WidthPercentageDefault = 12;

        let c_opt6 = new MyleTableColumnOptions();
        c_opt6.HeaderText = cacheStore.GetString("DescriptionLabel");
        c_opt6.FieldName = "Description";
        c_opt6.RowClickEnabled = true;
        c_opt6.IsSortable = true;
        c_opt6.SortColumn = DocumentSortOrderType.Description;
        c_opt6.SortDirection = SortDirection.ASC;
        c_opt6.WidthPercentageDefault = 17.1;

        let c_opt7 = new MyleTableColumnOptions();
        c_opt7.HeaderText = cacheStore.GetString("DoctorLabel");
        c_opt7.FieldName = "Doctor";
        c_opt7.RowClickEnabled = true;
        c_opt7.WidthPercentageDefault = 12;

        let c_opt8 = new MyleTableColumnOptions();
        c_opt8.HeaderText = cacheStore.GetString("StatusLabel");
        c_opt8.FieldName = "Status";
        c_opt8.RowClickEnabled = true;
        c_opt8.IsSortable = true;
        c_opt8.SortColumn = DocumentSortOrderType.Status;
        c_opt8.SortDirection = SortDirection.ASC;
        c_opt8.WidthPercentageDefault = 8;

        let c_opt9 = new MyleTableColumnOptions();
        c_opt9.HeaderText = cacheStore.GetString("ActionLabel");
        c_opt9.FieldName = "Action";
        c_opt9.WidthPixels = 270;
        c_opt9.IsFixedInPlace = true;
        c_opt9.WidthPercentage = 15;

        let c_opt10 = new MyleTableColumnOptions();
        if (this.isPortalEnabled) {
            c_opt10.HeaderText = cacheStore.GetString("StatusPortal");
            c_opt10.FieldName = "PortalStatus";
            c_opt10.RowClickEnabled = false;
            c_opt10.IsSortable = true;
            c_opt10.SortColumn = DocumentSortOrderType.PortalStatus;
            c_opt10.SortDirection = SortDirection.ASC;
            c_opt10.WidthPercentageDefault = 8;
        }

        let c_opt11 = new MyleTableColumnOptions();
        c_opt11.HeaderText = cacheStore.GetString("SiteDLabel");
        c_opt11.FieldName = "Site";
        c_opt11.RowClickEnabled = true;
        c_opt11.IsSortable = true;
        c_opt11.IsHidden = true;
        c_opt11.SortColumn = DocumentSortOrderType.Site;
        c_opt11.SortDirection = SortDirection.ASC;
        c_opt11.WidthPercentageDefault = 12;

        cols.push(c_opt1);
        cols.push(c_opt2);
        cols.push(c_opt3);
        cols.push(c_opt5);
        cols.push(c_opt6);
        cols.push(c_opt7);
        cols.push(c_opt8);
        cols.push(c_opt9);
        if (this.isPortalEnabled)
            cols.push(c_opt10);
        cols.push(c_opt11);

        return cols;
    }

    private tableRenderingOptions(ColumnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        let defaultTablePluginOptions = {
            MinColWidth: 85,
            MinTableWidth: 980,
            Cols: ColumnOptions.filter(k => k.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: true,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private onContainerClick(e: React.MouseEvent): void {
        e.stopPropagation();
    }

    private FilterTextChanged(text: string): void {
        sessionStore.dispatch(
            LocalOperations.Documents.SearchSettings_SearchBoxTextChanged,
            { value: text }
        );
    }

    private SetApproveSelectedDocuments(item: MedicalDocumentTableItem): void {
        let operation = new LocalEvent(LocalOperations.Documents.Mark_Document_ForApproval);
        operation.AddItem("Document", item);
        LocalEventDispatcher.dispatch(operation);
    }

    private SetApproveSelectedToAllDocuments(): void {
        this.isSelectAllDocument = !this.isSelectAllDocument;
        let operation = new LocalEvent(LocalOperations.Documents.Mark_All_Document_ForApproval);
        operation.AddItem("SelectAllValue", this.isSelectAllDocument);
        LocalEventDispatcher.dispatch(operation);
    }

    private PortalStatusClicked(item: MedicalDocumentTableItem, e: React.MouseEvent): void {
        $(e.target).closest("tr").addClass("active");

        let operation = new LocalEvent(LocalOperations.Documents.PortalStatusClicked);
        operation.AddItem("doc", item.Model);
        LocalEventDispatcher.dispatch(operation);
    }

    private onDSQLabsClick(): void {
        let op = new LocalEvent(LocalOperations.MainComponentViewOperations.DisplayDSQLabsGridPopup);
        op.AddItem("patientContactId", this.props.patient.IndividualResponse.Contact_id);
        MainComponentStateDispatcher.dispatch(op);
    }

    private onDSQImgsClick(): void {
        let op = new LocalEvent(LocalOperations.MainComponentViewOperations.DisplayDSQImgsGridPopup);
        op.AddItem("patientContactId", this.props.patient.IndividualResponse.Contact_id);
        MainComponentStateDispatcher.dispatch(op);
    }

    private taskRedirectClick(attachedCommunication: AttachedCommunication): void {
        sessionStore.dispatch(
            LocalPatientOperations.History.TaskRedirectFromEncounterOrLabs,
            {
                attachedCommunications: [attachedCommunication],
                isFromDocument: true
            }
        );
    }

    private taskRedirectClose(): void {
        sessionStore.dispatch(LocalPatientOperations.History.AttachedCommunicationsClose);
    }
}

export { PatientDocumentSearch };
